/**
 * 판으로 관리하는 글이 함께 쓰는 조각 — 지갑거래서명약관(EA-65~EA-67)·고객센터 안내(EA-70~EA-72)·
 * 거래 자료 메일 템플릿(EA-73~EA-75). 모두 같은 방식이다: 고치면 새 판, 옛 판은 지우지 않고, 시행 중인
 * 판이 없으면 서버가 404 를 준다.
 */
import type { ReactNode } from 'react'
import { ApiError } from '@/api/errors'
import { parseLocalDateTime } from '@/format'

/** 시행 중인 판 — 없으면(404) null(배지 없이 목록만, EA-42 약관 탭과 같게). */
export async function orNullOn404<T>(load: () => Promise<T>): Promise<T | null> {
  try {
    return await load()
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null
    throw err
  }
}

/**
 * 「시행일 (선택)」 칸의 값 → 밀리초. 칸은 「yyyy-mm-dd HH:mm:ss」 글이고 관리자 컴퓨터 시간으로 읽는다
 * (사장님 2026-10-01 — 관리자 웹의 시각 모양). 비우면 undefined(서버가 지금으로). 읽을 수 없으면 `bad`
 * (보내지 않는다).
 */
export function effectiveMillis(input: string): { ms: number | undefined; bad: boolean } {
  if (input.trim() === '') return { ms: undefined, bad: false }
  const ms = parseLocalDateTime(input)
  return ms === null ? { ms: undefined, bad: true } : { ms, bad: false }
}

/** 한 언어의 글 묶음(「한국어」·「영어」 칸). 안의 글은 [Plain] 으로 — HTML 로 그리지 않는다(EA-52 와 같게). */
export function LangText({ lang, children }: { lang: string; children: ReactNode }) {
  return (
    <section className="mb-3" aria-label={lang}>
      <h3 className="mb-1 text-xs font-semibold text-slate-500">{lang}</h3>
      <div className="rounded border border-slate-200 p-3 dark:border-slate-700">{children}</div>
    </section>
  )
}

/** 글 그대로(줄바꿈 유지). */
export function Plain({ text }: { text: string }) {
  return <div className="whitespace-pre-wrap">{text}</div>
}
